import React, { useMemo, useEffect } from 'react'; // EmailDetailsViewer component with static data
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import AttachmentsSection from './AttachmentsSection';
import { EmailApiService } from "../services/EmailApiService";
import { API_CONFIG, API_ENDPOINTS } from '../constants/apiConfig';
import * as XLSX from 'xlsx';

interface EmailDetailsViewerProps {
  onClose: () => void;
  emailId: string | number;
}

interface SummaryData {
  summary?: string;
  key_events?: string[];
  [key: string]: any;
}


function a11yProps(index: number) {
  return {
    id: `email-tab-${index}`,
    'aria-controls': `email-tabpanel-${index}`,
  };
}



export default function EmailDetailsViewer({
  onClose,
  emailId,
}: Readonly<EmailDetailsViewerProps>) {

const[emaildata,setEmailData] = React.useState<any>(null)
const [loading,setLoading] = React.useState<boolean>(false)
const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
const [uploadError, setUploadError] = React.useState<string | null>(null);
const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  // default active tab
  const [activeTab, setActiveTab] = React.useState(1);
  // refs
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const tabsRef = React.useRef<HTMLDivElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const isProgrammaticScroll = React.useRef<boolean>(false);
  const scrollGuardTimeout = React.useRef<number | null>(null);

 const sectionRefs = [
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null),
    React.useRef<HTMLDivElement | null>(null), 

    React.useRef<HTMLDivElement | null>(null), // For Actions tab
    React.useRef<HTMLDivElement | null>(null), // For Bot Request tab
    React.useRef<HTMLDivElement | null>(null), // For Bot Responsetab

  ];

  useEffect(() => {
  const fetchEmailDetails = async () => {
    try {
      setLoading(true);

      const emailService = new EmailApiService();

      const response = await emailService.getEmailById(emailId);
      

      setEmailData(response.data);
    } catch (error) {
      console.error("Failed to fetch email details", error);
    } finally {
      setLoading(false);
    }
  };

  if (emailId) {
    fetchEmailDetails();
  }
}, [emailId]);

  // parse summary safely
  const parsedSummary: SummaryData = useMemo(() => {
    const raw = emaildata?.data?.summary ?? emaildata?.summary;
    if (!raw) return { summary: '', key_events: [] };
    if (typeof raw === 'object') return raw as SummaryData;

    const text = String(raw).trim();
    if (!text.startsWith('{')) return { summary: text, key_events: [] };

    try {
      const parsed = JSON.parse(text);
      // the API sometimes double-encodes the payload
      const value = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
      return { summary: value?.summary || '', key_events: value?.key_events || [] };
    } catch (e) {
      console.error('Error parsing summary:', e);
      return { summary: text, key_events: [] };
    }
  }, [emaildata]);

  // formatting helpers
  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
      });
      return `${dateStr}, ${timeStr}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  
  const formatSummary = (text?: string) => {
    if (!text) return null;
    return (
      <ul style={{ margin: '8px 0', paddingLeft: '24px', listStyleType: 'disc' }}>
        {text
          .split('. ')
          .filter((sentence) => sentence.trim().length > 0)
          .map((sentence, index) => {
            const trimmed = sentence.trim();
            const key = `summary-${trimmed.substring(0, 20)}-${index}`; // Use content-based key with index as fallback
            return (
              <li key={key} style={{ marginBottom: '8px' }}>
                <Typography variant="body2" color="#161513">
                  {trimmed}
                  {!trimmed.endsWith('.') && !trimmed.endsWith('!') ? '.' : ''}
                </Typography>
              </li>
            );
          })}
      </ul>
    );
  };

  const formatKeyEvents = (events?: string[]) => {
    if (!events || !Array.isArray(events) || events.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" fontStyle="italic">
          No key events available
        </Typography>
      );
    }

    return (
      <ul style={{ margin: '8px 0', paddingLeft: '24px', listStyleType: 'disc', textAlign: 'left' }}>
        {events.map((event, index) => {
          const trimmed = event.trim();
          const key = `event-${trimmed.substring(0, 20)}-${index}`; // Use content-based key with index as fallback
          return (
            <li key={key} style={{ marginBottom: '12px' }}>
              <Typography variant="body2" color="#161513">
                {trimmed}
                {!trimmed.endsWith('.') && !trimmed.endsWith('!') ? '.' : ''}
              </Typography>
            </li>
          );
        })}
      </ul>
    );
  };

  const handleFileOpen = (url: string, fileName: string) => {
    if (!url) {
      alert("No file available for this attachment");
      return;
    }

    const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';

    if (fileExtension === 'pdf') {
      window.open(`/pdf-viewer?url=${encodeURIComponent(url)}`, "_blank");
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
      sessionStorage.setItem('imageViewer', JSON.stringify({ url, name: fileName }));
      window.open('/image-viewer', '_blank');
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      // Open Excel files in new tab using Microsoft Office Online Viewer
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      window.open(officeViewerUrl, "_blank");
    } else if (fileExtension === 'docx' || fileExtension === 'doc') {
      // Open Word files in new tab using Microsoft Office Online Viewer
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      window.open(officeViewerUrl, "_blank");
    } else {
      window.open(url, "_blank");
    }
  };

  // ---------------------------------------------------------------------------
  // Action button handlers
  // ---------------------------------------------------------------------------
  const handleDownloadAllFiles = async () => {
    if (!emaildata?.data?.attachments || emaildata.data.attachments.length === 0) return;

    const files: { url: string; fileName: string }[] = [];
    emaildata.data.attachments.forEach((attachment: any) => {
      const baseFileName = attachment.file_name?.replace(/\.[^/.]+$/, '') || 'file';
      // if (attachment.blob_url) {
      //   files.push({ url: attachment.blob_url, fileName: attachment.file_name || `${baseFileName}.pdf` });
      // }
      if (attachment.oriented_blob_url) {
        files.push({ url: attachment.oriented_blob_url, fileName: `${baseFileName}_oriented.pdf` });
      }
      if (attachment.output_excel_blob_url) {
        files.push({ url: attachment.output_excel_blob_url, fileName: `EB_Extraction_${baseFileName}.xlsx` });
      }
      if (attachment.output_pricing_calculator_blob_url) {
        const pricingFileName = decodeURIComponent(attachment.output_pricing_calculator_blob_url.split('?')[0].split('/').pop() || '');
        files.push({ url: attachment.output_pricing_calculator_blob_url, fileName: pricingFileName || `PricingCalculator_${baseFileName}.xlsx` });
      }
      if (attachment.output_word_blob_url) {
        files.push({ url: attachment.output_word_blob_url, fileName: `EstimatorReport_${baseFileName}.docx` });
      }
    });

    let hasError = false;
    for (const file of files) {
      try {
        const response = await fetch(file.url, { mode: 'cors' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = file.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        hasError = true;
        console.error(`Error downloading ${file.fileName}:`, error);
      }
    }

    if (hasError) {
      alert('Some files could not be downloaded. Azure Blob CORS must be enabled for cross-origin fetch.');
    }
  };

  const handleReUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setSuccessMessage(null);

    if (!file) {
      setSelectedFile(null);
      setUploadError(null);
      return;
    }

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setUploadError('Only .xlsx files are allowed for re-upload.');
      setSelectedFile(null);
      return;
    }

    const attachments = emaildata?.data?.attachments || [];
    const pricingAttachment = attachments.find((a: any) => a.output_pricing_calculator_blob_url);
    const baseFileName = pricingAttachment?.file_name?.replace(/\.[^/.]+$/, '') || 'file';
    const expectedFileName = pricingAttachment
      ? decodeURIComponent(pricingAttachment.output_pricing_calculator_blob_url.split('?')[0].split('/').pop() || '')
      : `PricingCalculator_${baseFileName}.xlsx`;

    if (pricingAttachment && file.name.toLowerCase() !== expectedFileName.toLowerCase()) {
      setUploadError(`Selected file name "${file.name}" does not match the current Pricing Calculator file "${expectedFileName}".`);
      setSelectedFile(null);
      return;
    }

    const requiredSheets = ['Calculator', 'Data', 'Phase 2 Audit'];
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', bookSheets: true });
        const actualSheets = workbook.SheetNames;

        const isValidOrder = actualSheets.length === requiredSheets.length &&
          actualSheets.every((name, index) => name === requiredSheets[index]);

        if (!isValidOrder) {
          // setUploadError(`The uploaded file's sheet order does not match. Expected: ${requiredSheets.join(', ')}. Found: ${actualSheets.join(', ') || 'none'}.`);
          setUploadError(`The uploaded Pricing Calculator does not match the required template.
One or more worksheet names have been changed. Please use the original worksheet names: ${requiredSheets.join(', ')}, then upload the file again.`);
          setSelectedFile(null);
          return;
        }

        setUploadError(null);
        setSelectedFile(file);
      } catch {
        setUploadError('Failed to parse the uploaded .xlsx file.');
        setSelectedFile(null);
      }
    };
    reader.onerror = () => {
      setUploadError('Failed to read the uploaded file.');
      setSelectedFile(null);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmitForProcessing = async () => {
    if (!selectedFile || uploadError || isSubmitting) {
      console.warn('No file selected for processing or file name mismatch');
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('email_data_id', String(emailId));

    try {
      const url = `${API_CONFIG.ANSWERS_BASE_URL}/${API_ENDPOINTS.EMAILS.REUPLOAD}`;
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Processing failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('Processing success:', data);
      setSuccessMessage('File submitted for processing successfully.');
      setTimeout(() => {
        window.location.reload();
      }, 6000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Processing request failed';
      console.error('Failed to submit for processing:', message);
      setSuccessMessage(null);
      alert(`Processing failed: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Helper: compute total offset (header + tabs). returns px number.
  const getTotalOffset = () => {
    const headerH = headerRef.current?.offsetHeight ?? 0;
    const tabsH = tabsRef.current?.offsetHeight ?? 0;
    return headerH + tabsH;
  };

  // ---------------------------------------------------------------------------
  // handle tab click -> scroll container to target section with offset
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);

    // Prevent the scroll-spy from overriding the clicked tab while the
    // programmatic smooth scroll is in flight (or if the section is too
    // short to reach the reference line).
    isProgrammaticScroll.current = true;
    if (scrollGuardTimeout.current) {
      window.clearTimeout(scrollGuardTimeout.current);
    }
    scrollGuardTimeout.current = window.setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 1200);

    const container = containerRef.current;
    const target = sectionRefs[newValue]?.current;
    if (!container || !target) return;

    // measure and compute scroll position so target sits below sticky header+tabs
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const totalOffset = getTotalOffset();

    const scrollTo =
      container.scrollTop + (targetRect.top - containerRect.top) - totalOffset + 8; // +8 gap
    container.scrollTo({ top: scrollTo, behavior: 'smooth' });
  };

  // Handle scroll events for fallback functionality
  const createScrollHandler = React.useCallback((container: HTMLElement) => {
    const handleScroll = () => {
      if (isProgrammaticScroll.current) {
        // Scrolling from a tab click: debounce the guard release so it only
        // unlocks ~250ms after the last scroll event (i.e. scroll end).
        if (scrollGuardTimeout.current) {
          window.clearTimeout(scrollGuardTimeout.current);
        }
        scrollGuardTimeout.current = window.setTimeout(() => {
          isProgrammaticScroll.current = false;
        }, 250);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const referenceY = containerRect.top + getTotalOffset() + 8;

      // Active section = the LAST section whose top has passed the reference
      // line. Short sections (e.g. an empty JSON block) stay correctly
      // selected even when the line falls inside the following section.
      let activeIndex = 0;
      for (let i = 0; i < sectionRefs.length; i++) {
        const ref = sectionRefs[i];
        if (!ref.current) continue;

        const rect = ref.current.getBoundingClientRect();
        if (rect.top <= referenceY) {
          activeIndex = i;
        } else {
          break;
        }
      }
      if (activeTab !== activeIndex) setActiveTab(activeIndex);
    };

    return handleScroll;
  }, [activeTab, sectionRefs]);

  // ---------------------------------------------------------------------------
  // IntersectionObserver + scroll fallback effect
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = createScrollHandler(container);

    // passive listener for better perf on mobile
    container.addEventListener('scroll', handleScroll, { passive: true });

    // initial alignment
    handleScroll();

    // cleanup
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [emaildata?.id, createScrollHandler, sectionRefs]); // recalc when email changes or dependencies change

  useEffect(() => {
    return () => {
      if (scrollGuardTimeout.current) {
        window.clearTimeout(scrollGuardTimeout.current);
      }
    };
  }, []);


  // Safely normalize JSON fields: handles objects, JSON strings, double-encoded
  // strings, null, and the literal "null" string the backend sometimes returns
  const parseJsonField = (value: any): Record<string, any> => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
      let parsed = JSON.parse(String(value));
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };
  const extractedFields = parseJsonField(emaildata?.data?.json_data);
  const extractedManifest = parseJsonField(emaildata?.data?.manifest_json);
  const extractedExtraction = parseJsonField(emaildata?.data?.extraction_json);
  const extractedAnswer = parseJsonField(emaildata?.data?.answer_json);
  const extractedMerged = parseJsonField(emaildata?.data?.merged_json);
  const extractedBotrequest = parseJsonField(emaildata?.data?.bot_request_json);
  const extractedBotresponse = parseJsonField(emaildata?.data?.bot_response_json);
  console.log(emaildata)
  // ---------------------------------------------------------------------------
  // Render
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' ,backgroundColor:'#ffff',borderRadius:'8px'}}>
      {/* Sticky Header with Title and Close Button */}
      <Box
        ref={headerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderRadius:'8px 8px 0px 0px'
        }}
      >
        <Typography variant="h6" noWrap>
          Email Details
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close drawer">
          <CloseIcon sx={{ fontSize: 20, color: 'rgb(29 28 28 / 70%)' }} />
        </IconButton>
      </Box>
       {successMessage && (
                  <Alert severity="success" variant="filled" sx={{ mb: 1 }}>
                    {successMessage}
                  </Alert>
                )}

      {/* Sticky Tabs */}
      <Box
        ref={tabsRef}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: (headerRef.current?.offsetHeight ?? 0) || 0, // keep tabs below header
          zIndex: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="email details tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#3A3541',
              height: 2,
            },
            '& .MuiTab-root': {
              minWidth: 'auto',
              padding: '12px 16px',
              fontSize: '0.875rem',
              color: '#3A3541',
              textTransform: 'none',
              '&:hover': {
                color: '#3A3541',
                opacity: 1,
              },
            },
            '& .Mui-selected': {
              color: '#3A3541 !important',
              fontWeight: 500,
            },
            '& .MuiTab-root .MuiSvgIcon-root': {
              color: 'rgb(29 28 28 / 70%)',
            },
            '& .MuiTabScrollButton-root .MuiSvgIcon-root': {
              color: 'black',
            },
          }}
        >
          
          <Tab label="Extractions" {...a11yProps(0)} />
          <Tab label="Summary" {...a11yProps(1)} />
          <Tab label="Key Events" {...a11yProps(2)} />
           <Tab label="Email Details" {...a11yProps(3)} />
          <Tab label="Email Body" {...a11yProps(4)} />
          <Tab label="Email Attachments" {...a11yProps(5)} />
         
          <Tab label="Error Message" {...a11yProps(6)} />
       
          <Tab label="Classification Json" {...a11yProps(7)} />
          <Tab label="Manifest JSON" {...a11yProps(8)} />
          <Tab label="Extraction JSON" {...a11yProps(9)} />
          <Tab label="Answer JSON" {...a11yProps(10)} />
          <Tab label="Merged JSON" {...a11yProps(11)} />
          <Tab label="Bot Request JSON" {...a11yProps(12)} />
          <Tab label="Bot Response JSON" {...a11yProps(13)} />
          
         
        </Tabs>
      </Box>

      {/* Scrollable content container */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          scrollBehavior: 'smooth',
          scrollSnapType: 'y mandatory',
          '& > div': {
            scrollSnapAlign: 'start',
            '& .MuiSvgIcon-root': {
              color: 'rgb(29 28 28 / 70%)',
            },
          },
        }}
      >
       
        
         {/* Extractions Section */}
        <div ref={sectionRefs[0]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                fontSize: '18px',
              }}
            >
              Extractions
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#F4F5F9', borderRadius: 2 }}>
             
              {emaildata?.data?.attachments && emaildata.data.attachments.length > 0 ? (
                (() => {
                  const hasOutputUrls = emaildata.data.attachments.some((attachment: any) =>
                    attachment.oriented_blob_url ||
                    attachment.output_excel_blob_url ||
                    attachment.output_word_blob_url ||
                    attachment.output_pricing_calculator_blob_url
                  );
                  return hasOutputUrls ? (
                <Box >
                  {emaildata.data.attachments.map((attachment: any, index: number) => {
                    const baseFileName = attachment.file_name?.replace(/\.[^/.]+$/, '') || 'file';
                    const pricingFileName = attachment.output_pricing_calculator_blob_url
                      ? decodeURIComponent(attachment.output_pricing_calculator_blob_url.split('?')[0].split('/').pop() || '')
                      : '';
                    
                    return (
                      <Box
                        key={attachment.id || index}
                        sx={{
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          textAlign: 'left',
                          overflowX:'auto'
                        }}
                      >
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          
                         
                          {/* Oriented File */}
                          <Box>
                          {attachment.oriented_blob_url && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" color="#666">Oriented:</Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#1a73e8',
                                  cursor: 'pointer',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                                onClick={() => handleFileOpen(attachment.oriented_blob_url, `${baseFileName}_oriented.pdf`)}
                              >
                                {baseFileName}_oriented.pdf
                              </Typography>
                            </Box>
                          )}
                          </Box>
                      
                          {/* Excel File */}
                          <Box>
                          {attachment.output_excel_blob_url && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" color="#666">Excel:</Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#1a73e8',
                                  cursor: 'pointer',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                                onClick={() => handleFileOpen(attachment.output_excel_blob_url, `EB_Extraction_${baseFileName}.xlsx`)}
                              >
                                EB_Extraction_{baseFileName}.xlsx
                              </Typography>
                            </Box>
                          )}
                          </Box>
                         
                          {/* Word File */}
                          <Box>
                          {attachment.output_word_blob_url && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" color="#666">Word:</Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#1a73e8',
                                  cursor: 'pointer',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                                onClick={() => handleFileOpen(attachment.output_word_blob_url, `EstimatorReport_${baseFileName}.docx`)}
                              >
                                EstimatorReport_{baseFileName}.docx
                              </Typography>
                            </Box>
                          )}
                          </Box>
                        
                          {/* Word File */}
                          <Box>
                          {attachment.output_pricing_calculator_blob_url && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" color="#666">Pricing Calculator:</Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#1a73e8',
                                  cursor: 'pointer',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                                onClick={() => handleFileOpen(attachment.output_pricing_calculator_blob_url, pricingFileName)}
                              >
                                {pricingFileName}
                              </Typography>
                            </Box>
                          )}
                          </Box>
                          
                        </Box>
                      </Box>
                    );
                  })}
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAllFiles}
                  >
                    Download All
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<UploadIcon />}
                    onClick={handleReUpload}
                  >
                    Re-upload
                  </Button>
                </Box>
                {selectedFile && (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                    Selected: {selectedFile.name}
                  </Typography>
                )}
                {uploadError && (
                  <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                    {uploadError}
                  </Typography>
                )}
               
                <Button
                  variant="contained"
                  fullWidth
                  // disabled={selectedFile || Boolean(uploadError)}
                  onClick={handleSubmitForProcessing}
                  sx={{
                    backgroundColor: !selectedFile || uploadError ? '#9e9e9e' : '#1a184d',
                    cursor: !selectedFile || uploadError ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      backgroundColor: !selectedFile || uploadError ? '#9e9e9e' : isSubmitting ? '#1a184d' : '#13123b',
                    },
                  }}
                >
                  {isSubmitting ? 'Submitting ...' : 'Submit for processing'}
                </Button>
              </Box>
                </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                     No Extractions Available
                    </Typography>
                  );
                })()
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No Extractions Available
                </Typography>
              )}
            </Box>
          </Box>
        </div>

         {/* Summary Section */}
        <div ref={sectionRefs[1]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                fontSize: '18px',
              }}
            >
              Summary
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#F4F5F9', borderRadius: 2, textAlign: 'left' }}>
              {parsedSummary?.summary ? (
                formatSummary(parsedSummary.summary)
              ) : (
                <Typography variant="body2" color="textSecondary" fontStyle="italic">
                  No summary available
                </Typography>
              )}
            </Box>
          </Box>
        </div>

        {/* Key Section */}
        <div ref={sectionRefs[2]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                fontSize: '18px',
              }}
            >
              Key Events
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#F4F5F9', borderRadius: 2, textAlign: 'left' }}>
              {formatKeyEvents(parsedSummary?.key_events)}
            </Box>
          </Box>
        </div>

       {/* Email Details Section */}
        <div ref={sectionRefs[3]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                fontSize: '18px',
              }}
            >
              Email Details
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#F4F5F9', borderRadius: 2 }}>
              <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={2}>
                <Box display="flex" gap={1}>
                  <Typography variant="subtitle2" color="#161513" fontWeight="bold">
                    Customer Name:
                  </Typography>
                 
                  <Typography variant="body2"> {emaildata?.data?.customer_name || "N/A"}</Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Typography variant="subtitle2" color="#161513" fontWeight="bold">
                    From:
                  </Typography>
                  <Typography variant="body2">{emaildata?.data?.from_email || 'N/A'}</Typography>
                </Box>
               
                <Box display="flex" gap={1}>
                  <Typography variant="subtitle2" color="#161513" fontWeight="bold">
                    Received:
                  </Typography>
                  <Typography variant="body2">{formatDate(emaildata?.data?.received_utc)}</Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Typography variant="subtitle2" color="#161513" fontWeight="bold">
                    Created At:
                  </Typography>
                  <Typography variant="body2">{formatDate(emaildata?.data?.created_at)}</Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Typography variant="subtitle2" color="#161513" fontWeight="bold">
                    Created By:
                  </Typography>
                  <Typography variant="body2">{emaildata?.data?.created_by || 'N/A'}</Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Typography variant="subtitle2" color="#161513" fontWeight="bold">
                    Updated At:
                  </Typography>
                  <Typography variant="body2">{formatDate(emaildata?.data?.updated_at)}</Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Typography variant="subtitle2" color="#161513" fontWeight="bold">
                    Updated By:
                  </Typography>
                  <Typography variant="body2">{emaildata?.data?.updated_by || 'N/A'}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </div>

        {/* Email Section */}
        <div ref={sectionRefs[4]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                pl: 0,
                fontSize: '18px',
              }}
            >
              Email Body
            </Typography>
            <Box sx={{ p: 2, textAlign: 'left', overflow: 'auto', backgroundColor: '#F4F5F9', borderRadius: 2 }}>
             

              <Box
                sx={{
                  textAlign: 'left',
                  '& img': { maxWidth: '100%' },
                  '& a': { wordBreak: 'break-all' },
                  '& p, & div': {
                    textAlign: 'left',
                    margin: 0,
                    padding: 0,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: emaildata?.data?.email_body || '<p>No content available</p>',
                }}
              />
            </Box>
          </Box>
        </div>

         {/* Email Attachments Section */}
        <div ref={sectionRefs[5]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                fontSize: '18px',
              }}
            >
              Email Attachments
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#F4F5F9', borderRadius: 2 }}>
              {emaildata?.data?.attachments && emaildata.data.attachments.length > 0 ? (
                <Box>
                  {emaildata.data.attachments.map((attachment: any, index: number) => {
                    const fileName = attachment.file_name || 'file';
                    
                    return (
                      <Box
                        key={attachment.id || index}
                        sx={{
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 2 }}>
                          {/* Input File */}
                          {attachment.blob_url && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" color="#666">Input:</Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#1a73e8',
                                  cursor: 'pointer',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                                onClick={() => handleFileOpen(attachment.blob_url, fileName)}
                              >
                                {fileName}
                              </Typography>
                            </Box>
                          )}
                          
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No attachments available
                </Typography>
              )}
            </Box>
          </Box>
        </div>
     

        {/* Error Message Section */}
        <div ref={sectionRefs[6]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                fontSize: '18px',
              }}
            >
              Error Message
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#F4F5F9', borderRadius: 2, textAlign: 'left' }}>
              {emaildata?.data?.status_message ? (
                <Typography variant="body2" color="#161513">
                  {emaildata.data.status_message}
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary" fontStyle="italic">
                  No error message available
                </Typography>
              )}
            </Box>
          </Box>
        </div>

        



{/* JSON Data Section */}
        <div ref={sectionRefs[7]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                px: 2,
                fontSize: '18px',
              }}
            >
              Classification JSON
            </Typography>
             {Object.keys(extractedFields).length > 0 ? (
              <Box sx={{ p: 2, textAlign: 'left', backgroundColor: '#F4F5F9', borderRadius: 2 }}>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontFamily: 'monospace',
                    maxHeight: '60vh',
                    overflow: 'auto',
                    padding: '8px',
                    borderRadius: '4px',
                  }}
                >
                  {JSON.stringify(extractedFields, null, 2)}
                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No extracted data available
              </Typography>
            )} 
          </Box>
        </div>

        {/* Manifest JSON */}
 <div ref={sectionRefs[8]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                px: 2,
                fontSize: '18px',
              }}
            >
              Manifest JSON
            </Typography>
             {Object.keys(extractedManifest).length > 0 ? (
              <Box sx={{ p: 2, textAlign: 'left', backgroundColor: '#F4F5F9', borderRadius: 2 }}>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontFamily: 'monospace',
                    maxHeight: '60vh',
                    overflow: 'auto',
                    padding: '8px',
                    borderRadius: '4px',
                  }}
                >
                  {JSON.stringify(extractedManifest, null, 2)}
                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No extracted data available
              </Typography>
            )} 
          </Box>
        </div>

         {/* Extraction JSON*/}
 <div ref={sectionRefs[9]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                px: 2,
                fontSize: '18px',
              }}
            >
             Extraction JSON
            </Typography>
             {Object.keys(extractedExtraction).length > 0 ? (
              <Box sx={{ p: 2, textAlign: 'left', backgroundColor: '#F4F5F9', borderRadius: 2 }}>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontFamily: 'monospace',
                    maxHeight: '60vh',
                    overflow: 'auto',
                    padding: '8px',
                    borderRadius: '4px',
                  }}
                >
                  {JSON.stringify(extractedExtraction, null, 2)}
                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No extracted data available
              </Typography>
            )} 
          </Box>
        </div>


         {/* Answer JSON */}
 <div ref={sectionRefs[10]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                px: 2,
                fontSize: '18px',
              }}
            >
              Answer JSON
            </Typography>
             {Object.keys(extractedAnswer).length > 0 ? (
              <Box sx={{ p: 2, textAlign: 'left', backgroundColor: '#F4F5F9', borderRadius: 2 }}>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontFamily: 'monospace',
                    maxHeight: '60vh',
                    overflow: 'auto',
                    padding: '8px',
                    borderRadius: '4px',
                  }}
                >
                  {JSON.stringify(extractedAnswer, null, 2)}
                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No extracted data available
              </Typography>
            )} 
          </Box>
        </div>

          {/* Merged JSON */}
 <div ref={sectionRefs[11]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                px: 2,
                fontSize: '18px',
              }}
            >
              Merged JSON
            </Typography>
             {Object.keys(extractedMerged).length > 0 ? (
              <Box sx={{ p: 2, textAlign: 'left', backgroundColor: '#F4F5F9', borderRadius: 2 }}>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontFamily: 'monospace',
                    maxHeight: '60vh',
                    overflow: 'auto',
                    padding: '8px',
                    borderRadius: '4px',
                  }}
                >
                  {JSON.stringify(extractedMerged, null, 2)}
                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No extracted data available
              </Typography>
            )} 
          </Box>
        </div>
            {/* Bot Request JSON */}
 <div ref={sectionRefs[12]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                px: 2,
                fontSize: '18px',
              }}
            >
              Bot Request JSON
            </Typography>
             {Object.keys(extractedBotrequest).length > 0 ? (
              <Box sx={{ p: 2, textAlign: 'left', backgroundColor: '#F4F5F9', borderRadius: 2 }}>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontFamily: 'monospace',
                    maxHeight: '60vh',
                    overflow: 'auto',
                    padding: '8px',
                    borderRadius: '4px',
                  }}
                >
                  {JSON.stringify(extractedBotrequest, null, 2)}
                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No Bot Request data available
              </Typography>
            )} 
          </Box>
                  
        </div>
                  {/* Bot Response JSON */}
 <div ref={sectionRefs[13]}>
          <Box p={2}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                textAlign: 'left',
                mb: 2,
                color: 'text.primary',
                px: 2,
                fontSize: '18px',
              }}
            >
              Bot Response JSON
            </Typography>
             {Object.keys(extractedBotresponse).length > 0 ? (
              <Box sx={{ p: 2, textAlign: 'left', backgroundColor: '#F4F5F9', borderRadius: 2 }}>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontFamily: 'monospace',
                    maxHeight: '60vh',
                    overflow: 'auto',
                    padding: '8px',
                    borderRadius: '4px',
                  }}
                >
                  {JSON.stringify(extractedBotresponse, null, 2)}
                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No extracted data available
              </Typography>
            )} 
          </Box>
        </div>

        {/* Bottom spacer so the last sections can scroll up to the reference line */}
        <div style={{ height: '70vh' }} aria-hidden="true" />

      </Box>
    </Box>
  );
}
